import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { useToast } from '@chakra-ui/react';
import { MultiTabManager } from '../utils/multiTabManager.ts';
import i18n from '../config/i18n.ts';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  joinMultiplayerGame: (lobbyCode: string, playerName: string, uid?: string) => void;
  startMultiplayerGame: (lobbyCode: string, rules: any, players: any[], currentUser: any) => void;
  selectCategory: (lobbyCode: string, categoryId: string) => void;
  answerQuestion: (lobbyCode: string, answer: string) => void;
  endGameManually: (lobbyCode: string, playerName: string) => void;
  updateLobbyRules: (lobbyCode: string, rules: any) => void;
  reconnectToGame: (lobbyCode: string, playerName: string) => void;
  onLobbyRulesUpdated: (callback: (data: { rules: any }) => void) => void;
  offLobbyRulesUpdated: (callback: (data: { rules: any }) => void) => void;
  onRedirectToMultiplayerGame: (callback: (data: { lobbyCode: string; players: any[]; rules?: any }) => void) => void;
  offRedirectToMultiplayerGame: (callback: (data: { lobbyCode: string; players: any[]; rules?: any }) => void) => void;
  onPlayerDisconnected: (callback: (data: { playerName: string; playerIndex: number }) => void) => void;
  offPlayerDisconnected: (callback: (data: { playerName: string; playerIndex: number }) => void) => void;
  onPlayerReconnected: (callback: (data: { playerName: string; playerIndex: number }) => void) => void;
  offPlayerReconnected: (callback: (data: { playerName: string; playerIndex: number }) => void) => void;
  onGameReconnected: (callback: (data: any) => void) => void;
  offGameReconnected: (callback: (data: any) => void) => void;
  onLobbyOwnerChanged: (callback: (data: { newOwner: string }) => void) => void;
  offLobbyOwnerChanged: (callback: (data: { newOwner: string }) => void) => void;
  returnToLobby: (lobbyCode: string) => void;
  onGameResetToLobby: (callback: (data: { lobbyCode: string; message: string }) => void) => void;
  offGameResetToLobby: (callback: (data: { lobbyCode: string; message: string }) => void) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [tabManager] = useState(() => new MultiTabManager());
  const toast = useToast();

  useEffect(() => {
    // @ts-ignore
    const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
    
    let newSocket: Socket;
    let cancelled = false;
    
    // Dynamic import pour éviter que socket.io-client (~65KB gzip) soit dans le main bundle
    import('socket.io-client').then(({ io }) => {
      if (cancelled) return;
      newSocket = io(API_URL);
    
    newSocket.on('connect', () => {
      console.log('🔌 Connecté au serveur Socket.IO');
      console.log(`🆔 ID de cet onglet: ${tabManager.getTabId()}`);
      
      setIsConnected(true);
      
      // Avertir si plusieurs onglets sont ouverts
      if (tabManager.isMultipleTabsOpen()) {
        console.warn(`⚠️ ${tabManager.getActiveTabsCount()} onglets détectés.`);
        
        toast({
          title: "Onglets multiples détectés",
          description: `${tabManager.getActiveTabsCount()} onglets sont ouverts. Si vous rencontrez des problèmes de connexion, fermez les autres onglets.`,
          status: "info",
          duration: 8000,
          isClosable: true,
          position: "top"
        });
      }
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Déconnecté du serveur Socket.IO');
      setIsConnected(false);
    });

    // Gérer le remplacement de connexion
    newSocket.on('connection-replaced', (data: { message: string }) => {
      console.log('⚠️ Connexion remplacée:', data.message);
      
      // Afficher une notification toast
      toast({
        title: "Connexion fermée",
        description: data.message,
        status: "warning",
        duration: 8000,
        isClosable: true,
        position: "top"
      });
      
      // Rediriger vers la page d'information après un court délai
      setTimeout(() => {
        window.location.href = '/connection-replaced';
      }, 2000);
    });

    // Gérer le rejet de connexion (nouvelle approche)
    newSocket.on('connection-rejected', (data: { message: string }) => {
      console.log('🚫 Connexion rejetée:', data.message);
      
      // Afficher une notification toast plus explicite
      toast({
        title: "Connexion refusée",
        description: "Vous avez déjà un onglet ouvert avec ce compte. Fermez les autres onglets et réessayez.",
        status: "error",
        duration: 12000,
        isClosable: true,
        position: "top"
      });
      
      // Rediriger vers la page d'information après un court délai
      setTimeout(() => {
        window.location.href = '/connection-rejected';
      }, 3000);
    });

    setSocket(newSocket);
    }); // end dynamic import

    return () => {
      cancelled = true;
      if (newSocket) {
        newSocket.close();
      }
      tabManager.destroy();
    };
  }, [toast, tabManager]);

  const joinMultiplayerGame = (lobbyCode: string, playerName: string, uid?: string) => {
    if (socket) {
      socket.emit('join-multiplayer-game', { lobbyCode, playerName, uid, language: i18n.language });
    }
  };

  const startMultiplayerGame = (lobbyCode: string, rules: any, players: any[], currentUser: any) => {
    if (socket) {
      // Ajouter l'UID Firebase aux données du joueur actuel
  const playersWithUID = players.map(player => {
        if (player.name === currentUser?.displayName) {
          return {
            ...player,
            uid: currentUser?.uid || null
          };
        }
        return player;
      });
      
      socket.emit('start-multiplayer-game', { lobbyCode, rules, players: playersWithUID, language: i18n.language });
    }
  };

  const selectCategory = (lobbyCode: string, categoryId: string) => {
    if (socket) {
      socket.emit('select-category', { lobbyCode, categoryId });
    }
  };

  const answerQuestion = (lobbyCode: string, answer: string) => {
    if (socket) {
      socket.emit('answer-question', { lobbyCode, answer });
    }
  };

  const endGameManually = (lobbyCode: string, playerName: string) => {
    if (socket) {
      socket.emit('end-game-manually', { lobbyCode, playerName });
    }
  };

  const updateLobbyRules = (lobbyCode: string, rules: any) => {
    if (socket) {
      socket.emit('update-lobby-rules', { lobbyCode, rules });
    }
  };

  const reconnectToGame = (lobbyCode: string, playerName: string) => {
    if (socket) {
      socket.emit('reconnect-to-game', { lobbyCode, playerName });
    }
  };

  const onLobbyRulesUpdated = (callback: (data: { rules: any }) => void) => {
    if (socket) {
      socket.on('lobby-rules-updated', callback);
    }
  };

  const offLobbyRulesUpdated = (callback: (data: { rules: any }) => void) => {
    if (socket) {
      socket.off('lobby-rules-updated', callback);
    }
  };

  const onRedirectToMultiplayerGame = (callback: (data: { lobbyCode: string; players: any[] }) => void) => {
    if (socket) {
      socket.on('redirect-to-multiplayer-game', callback);
    }
  };

  const offRedirectToMultiplayerGame = (callback: (data: { lobbyCode: string; players: any[] }) => void) => {
    if (socket) {
      socket.off('redirect-to-multiplayer-game', callback);
    }
  };

  const onPlayerDisconnected = (callback: (data: { playerName: string; playerIndex: number }) => void) => {
    if (socket) {
      socket.on('player-disconnected', callback);
    }
  };

  const offPlayerDisconnected = (callback: (data: { playerName: string; playerIndex: number }) => void) => {
    if (socket) {
      socket.off('player-disconnected', callback);
    }
  };

  const onPlayerReconnected = (callback: (data: { playerName: string; playerIndex: number }) => void) => {
    if (socket) {
      socket.on('player-reconnected', callback);
    }
  };

  const offPlayerReconnected = (callback: (data: { playerName: string; playerIndex: number }) => void) => {
    if (socket) {
      socket.off('player-reconnected', callback);
    }
  };

  const onGameReconnected = (callback: (data: any) => void) => {
    if (socket) {
      socket.on('game-reconnected', callback);
    }
  };

  const offGameReconnected = (callback: (data: any) => void) => {
    if (socket) {
      socket.off('game-reconnected', callback);
    }
  };

  const onLobbyOwnerChanged = (callback: (data: { newOwner: string }) => void) => {
    if (socket) {
      socket.on('lobby-owner-changed', callback);
    }
  };

  const offLobbyOwnerChanged = (callback: (data: { newOwner: string }) => void) => {
    if (socket) {
      socket.off('lobby-owner-changed', callback);
    }
  };

  const returnToLobby = (lobbyCode: string) => {
    if (socket) {
      console.log('🏠 Émission de return-to-lobby pour:', lobbyCode);
      socket.emit('return-to-lobby', { lobbyCode });
    }
  };

  const onGameResetToLobby = (callback: (data: { lobbyCode: string; message: string }) => void) => {
    if (socket) {
      socket.on('game-reset-to-lobby', callback);
    }
  };

  const offGameResetToLobby = (callback: (data: { lobbyCode: string; message: string }) => void) => {
    if (socket) {
      socket.off('game-reset-to-lobby', callback);
    }
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        joinMultiplayerGame,
        startMultiplayerGame,
        selectCategory,
        answerQuestion,
        endGameManually,
        updateLobbyRules,
        reconnectToGame,
        onLobbyRulesUpdated,
        offLobbyRulesUpdated,
        onRedirectToMultiplayerGame,
        offRedirectToMultiplayerGame,
        onPlayerDisconnected,
        offPlayerDisconnected,
        onPlayerReconnected,
        offPlayerReconnected,
        onGameReconnected,
        offGameReconnected,
        onLobbyOwnerChanged,
        offLobbyOwnerChanged,
        returnToLobby,
        onGameResetToLobby,
        offGameResetToLobby,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
